import {
  BooleanField,
  DateField,
  DateTimeField,
  IModelContext,
  ModelDependency,
  ModelGroup,
  NumberField,
  StringField
} from '@mng/model';
import { createVermittlerInfoBlattAbberGroup, createVermittlerInformationGroup } from '../allgemein/vermittler-model';
import {
  gvFzKontoExpiryValidationFunction,
  gvMustRecalculateValidation,
  gvTarifeVorhandenValidation,
  gvVnChangedValidationFunction,
  vertragsAenderungAfterTarifVersionStichtagValidationFunction
} from './gvangebot.validation';
import {
  createGvAusgefuehrteGeschaeftsfaelleModelGroup,
  createGvBestandErlaubteGeschaeftsfaelleModelGroup,
  createGvErlaubteGeschaeftsfaelleModelGroup
} from './gvgeschaeftsfall.model';
import {
  createGvPersonenDataModelGroup,
  createGvVertrag2BestandPersonenDataModelGroup,
  createGvVertragBestandPersonenDataModelGroup
} from './gvperson.model';
import { createFzKontoFields } from './gvtarif.model';
import { createGvVertragInfoModelGroup } from './gvvertrag.model';
import { hasFzKontoChanged, hasVnChanged } from './logic/gvangebot-model.logic';

export function createGvAngebotModel(): ModelGroup {
  const vermittlerGroup: ModelGroup = createVermittlerInformationGroup();
  const vermittlerInfoBlattAbberGroup: ModelGroup = createVermittlerInfoBlattAbberGroup();

  const model: ModelGroup = new ModelGroup('gvAngebotData')
    .addItems(
      new StringField('id').withReadOnly(),
      new StringField('userRights').asArray(),
      new DateTimeField('modifiedDate').withReadOnly(),
      new BooleanField('mustRecalculate').withReadOnly(),
      new BooleanField('vertragsAenderung').withReadOnly(),
      new BooleanField('leistungsfall').withReadOnly(),
      new StringField('aktenTrennung').withReadOnly(),
      new StringField('globalWarning').withReadOnly(),
      new BooleanField('aktenZusammenlegung').withReadOnly(),
      new BooleanField('iddBeratungDurchgefuehrt').withReadOnly(),
      new BooleanField('vertragsAenderungInitialisiert').withReadOnly(),
      new DateField('vertragsAenderungsDatum', 'Datum an dem der Vertrag geändert werden soll')
        .withFunctionDependency(isFzKontoChanged)
        .withFunctionDependency(isVnChanged)
        .withValidation(gvFzKontoExpiryValidationFunction)
        .withValidation(gvVnChangedValidationFunction)
        .withValidation(vertragsAenderungAfterTarifVersionStichtagValidationFunction)
        .withReadOnly(),
      new DateField('vertragszustandPerDatum', 'Datum an dem der Vertragszustand ausgelesen wurde').withReadOnly(),
      new DateField('todesDatumAlterVn', 'Sterbedatum des bisherigen VN')
        .withFunctionDependency(isVnChanged, false)
        .withReadOnly(),
      new DateField('tarifVersionStichtag', 'Stichtag für Tarifversion').withReadOnly(),
      vermittlerGroup,
      vermittlerInfoBlattAbberGroup,
      createGvAngebotFzKontoModelGroup(),
      createGvAngebotBestandFzKontoModelGroup(),
      createGvAngebotConfigModelGroup()
    )
    .withInterfaceName('GvAngebotData')
    .withValidation(gvTarifeVorhandenValidation)
    .withValidation(gvMustRecalculateValidation);
  model.addGroupObject(createGvPersonenDataModelGroup());
  model.addGroupObject(
    createGvVertragBestandPersonenDataModelGroup().withImportDef({
      localImport: true
    })
  );
  model.addGroupObject(
    createGvVertrag2BestandPersonenDataModelGroup().withImportDef({
      localImport: true
    })
  );
  model.addGroupObject(createGvVertragInfoModelGroup());
  model.addArrayGroupObject(createGvErlaubteGeschaeftsfaelleModelGroup());
  model.addArrayGroupObject(
    createGvBestandErlaubteGeschaeftsfaelleModelGroup().withImportDef({
      localImport: true
    })
  );
  model.addArrayGroupObject(
    createGvAusgefuehrteGeschaeftsfaelleModelGroup().withImportDef({
      localImport: true
    })
  );
  return model;
}

function createGvAngebotFzKontoModelGroup(): ModelGroup {
  return new ModelGroup('fzKonto', 'FZ Konto').withInterfaceName('GvAngebotFzKonto').addItems(...createFzKontoFields());
}

function createGvAngebotBestandFzKontoModelGroup(): ModelGroup {
  return new ModelGroup('bestandFzKonto', 'FZ Konto aus dem Vertrags-Bestand')
    .withInterfaceName('GvAngebotFzKonto')
    .withImportDef({
      localImport: true
    })
    .withReadOnly()
    .addItems(...createFzKontoFields());
}

function createGvAngebotConfigModelGroup(): ModelGroup {
  const config: ModelGroup = new ModelGroup('config', 'Konfiguration')
    .withInterfaceName('GvConfig')
    .addItems(
      new NumberField('stichtagVersBeginn', 'Stichtag Versicherungsbeginn-Rückdatierung'),
      new NumberField(
        'stichdatumMaxOffset',
        'Anzahl der Wochen die der tarifVersionStichtag nach vor bzw. hinten verändert werden kann'
      ),
      new NumberField(
        'gvExtraWeeksFormaleAenderungImLeistungsfall',
        'Anzahl der Wochen die bei formaler Änderung im Leistungsfall der Versicheerungsbeginn zusätzlich zurückdatiert werden darf'
      ),
      createGvValidityExceptionDatesModelGroup()
    );
  return config;
}

function createGvValidityExceptionDatesModelGroup(): ModelGroup {
  const config: ModelGroup = new ModelGroup('gvValidityExceptionDates', 'Gültigkeits-Ausnahme-Intervall')
    .withInterfaceName('GvValidityExceptionDates')
    .addItems(
      new StringField('from', 'Beginn spezielles Gültigkeit-Intervall'),
      new StringField('to', 'Ende spezielles Gültigkeit-Intervall'),
      new StringField('fixed', 'Fixes Gültig-Bis Datum')
    );
  return config;
}

function isFzKontoChanged(dependency: ModelDependency, modelContext: IModelContext): boolean {
  return hasFzKontoChanged(modelContext.dataStack[0]);
}

function isVnChanged(dependency: ModelDependency, modelContext: IModelContext): boolean {
  return hasVnChanged(modelContext.dataStack[0]);
}
